import { NoDataComponent, TableComponent, CustomButton } from '@/shared/ui/components';
import { BiodirectNoDataIcon, emptyIcon, inforBlueIcon } from '@/shared/ui/assets';
import { auth, handleScrollTab } from '@/shared/lib';
import classNames from 'classnames';
import { memo } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import {
  actionFormater,
  analysisTimeFormatter,
  dateOfServiceFormatter,
  generatedByFormatter,
  generatedTimeFormatter,
  reportIdFormatter,
  statusFormatterWithActionFormatter,
} from '../../lib/instantReportTab';
import NotAvailableComponent from '../notAvailableComponent/notAvailableComponent';
import { useInstantReportTab } from './useInstantReportTab';
import CreateInstantReportModalComponent from './createInstantReportModal/createInstantReportModal';
import type { InstantReportProps } from './instantReportTab.types';
import './instantReportTab.scss';

const CreateInstantReportModal = CreateInstantReportModalComponent as any;

const InstantReportTab = ({
  isActive,
  data,
  isLoadedData,
  hasLoginBanner,
  adjustedStudyDayOffset,
  isC1cProviderStudy = false,
}: InstantReportProps) => {
  const {
    state,
    isAbortedStudy,
    isArchivedStudy,
    isIcorDevice,
    onClickReport,
    handleOpenCreateInstantReportModal,
    handleCancelCreateInstantReportModal,
    handleAfterCreatingInstantReport,
    handleTableChange,
    handleViewExistedInstantReport,
  } = useInstantReportTab({ isActive, data, isLoadedData, isC1cProviderStudy });

  /**
   * Defines the instant report table columns: report id, date of service, generated time, analysis time, generated by, status and a trailing action column. Every render forwards the row arguments to its formatter along with the loaded flag, plus a seconds unit for analysis time and the report click handler for the action column.
   *
   * @returns Column definitions for the instant report table.
   */
  const generateColumns = () => [
    {
      dataIndex: 'reportId',
      title: t`Report ID`,
      render: (...renderProps: any[]) =>
        (reportIdFormatter as any)(...renderProps, { isLoadedData: state.isLoadedData }),
    },
    {
      dataIndex: 'dateOfService',
      title: t`Date of service`,
      render: (...renderProps: any[]) =>
        (dateOfServiceFormatter as any)(...renderProps, { isLoadedData: state.isLoadedData }),
    },
    {
      dataIndex: 'generatedTime',
      title: t`Generated time`,
      render: (...renderProps: any[]) =>
        (generatedTimeFormatter as any)(...renderProps, { isLoadedData: state.isLoadedData }),
    },
    {
      dataIndex: 'timeMonitored',
      title: t`Analysis time`,
      render: (...renderProps: any[]) =>
        (analysisTimeFormatter as any)(...renderProps, {
          isLoadedData: state.isLoadedData,
          unit: 'seconds',
        }),
    },
    {
      dataIndex: 'createdBy',
      title: t`Generated by`,
      render: (...renderProps: any[]) =>
        (generatedByFormatter as any)(...renderProps, { isLoadedData: state.isLoadedData }),
    },
    {
      dataIndex: 'status',
      title: t`Status`,
      render: (...renderProps: any[]) =>
        (statusFormatterWithActionFormatter as any)(...renderProps, {
          isLoadedData: state.isLoadedData,
        }),
    },
    {
      dataIndex: '',
      title: t`Actions`,
      width: 100,
      render: (...renderProps: any[]) =>
        (actionFormater as any)(...renderProps, {
          isLoadedData: state.isLoadedData,
          onClickReport,
        }),
    },
  ];

  if (isC1cProviderStudy) {
    return (
      <NotAvailableComponent
        containerClassName="instant-reports-tab"
        message={t`Instant reports are not available for external studies.`}
        hasLoginBanner={hasLoginBanner}
      />
    );
  }

  if (isIcorDevice) {
    return (
      <NotAvailableComponent
        containerClassName="instant-reports-tab"
        message={t`Instant reports are not available for studies running on the iCOR device.`}
        hasLoginBanner={hasLoginBanner}
      />
    );
  }

  if (!isLoadedData) {
    return (
      <div
        className={classNames('instant-reports-tab animated fadeIn', {
          'has-marketing-banner': auth.hasMarketingBanner(),
          'has-login-banner': hasLoginBanner,
        })}
      >
        <div className="custom-body">
          <TableComponent isEndOfData data={[]} columns={generateColumns()} isLoading />
        </div>
      </div>
    );
  }

  if (data.isTest) {
    return (
      <div
        className={classNames('instant-reports-tab --is-archived animated fadeIn', {
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
          'has-login-banner': hasLoginBanner,
        })}
      >
        <div className="no-data-wrapper">
          <div className="no-data">
            <NoDataComponent
              message={t`This feature isn't available for Test Study.`}
              icon={<ReactSVG src={emptyIcon} />}
            />
          </div>
        </div>
      </div>
    );
  }

  if (data.isBiofluxDirect && !data.start) {
    return (
      <NotAvailableComponent
        containerClassName="instant-reports-tab"
        message={t`This study is running on Biodirect service and has not been started yet. Please hold until the study starts.`}
        hasLoginBanner={hasLoginBanner}
      />
    );
  }

  if (!data.stop) {
    return (
      <div
        className={classNames('instant-reports-tab not-started animated fadeIn', {
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
          'has-login-banner': hasLoginBanner,
        })}
      >
        <div className="no-data-wrapper">
          <div className="no-data">
            <NoDataComponent
              message={
                <>
                  <div>{t`This study has not been started yet.`}</div>
                  <div>{t`Please hold until the study starts.`}</div>
                </>
              }
              icon={<ReactSVG src={BiodirectNoDataIcon} />}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames('instant-reports-tab animated fadeIn', {
        'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
        'has-login-banner': hasLoginBanner,
      })}
      onScroll={handleScrollTab}
    >
      <CreateInstantReportModal
        open={state.isOpenCreateInstantReportModal}
        onCancel={handleCancelCreateInstantReportModal}
        handleAfterCreatingInstantReport={handleAfterCreatingInstantReport}
        studyId={data.id}
        start={data.start}
        stop={data.stop}
        timezone={data.timezone}
        studyTimeOffset={data.studyTimeOffset}
        adjustedStudyDayOffset={adjustedStudyDayOffset}
        studyFid={data.friendlyId}
        handleViewExistedInstantReport={handleViewExistedInstantReport}
      />

      {isArchivedStudy && (
        <div className="archived-study-container-instant">
          <img src={inforBlueIcon} alt="inforBlueIcon" />
          {t`The creation of instant report feature has been disabled as this study has been archived. If you need support to create an instant report for this study, please contact the Biotricity customer support team.`}
        </div>
      )}
      <div className="button-container">
        {state.hideElementInitial ? (
          <div />
        ) : (
          <CustomButton
            id="create-report-btn"
            tooltipTitle={
              isAbortedStudy
                ? t`Cannot create report for an aborted study.`
                : isArchivedStudy && t`This study is archived`
            }
            size="small"
            onClick={handleOpenCreateInstantReportModal}
            disabled={isAbortedStudy || isArchivedStudy}
          >
            {t`Generate Report`}
          </CustomButton>
        )}
      </div>
      <div className="custom-body">
        {state.reports.length > 0 || (!state.reports.length && !state.isLoadedData) ? (
          <TableComponent
            isEndOfData
            data={state.reports}
            total={state.reports.length}
            columns={generateColumns()}
            currentPage={state.page}
            pageSize={state.pageSize}
            handleTableChange={handleTableChange}
            isLoading={!state.isLoadedData}
          />
        ) : (
          <div className="no-data-wrapper">
            <div className="no-data">
              <NoDataComponent />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(InstantReportTab);
